import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Form, Pagination, Row, Select, Space, Table, Typography, message, Modal as AntModal, Col } from "antd";
import type { PaginationProps, TableProps } from "antd";
import "./ReportVolumeTable.css";
import { usePagination } from "src/hook/usePagination";
import { useDidMount } from "src/hook/useDidMount";
import { useShallow } from "zustand/react/shallow";
import { profileStore } from "src/stores/profile-store";
import { flareVolumeStore } from "src/stores/flare-volume-store";
import { utilsStore } from "src/stores/utils-store";
import { omit, isEmpty, cloneDeep, omitBy, isNull } from "lodash";
import { reportFlareStore } from "src/stores/report-flare-volume-store";
import { FilterReportFlareVolume, ResponseReportFlareVolume } from "src/entity/flare/report-flare-volume.entity";
import { flareCaseFilter, GAS_UNIT } from "src/entity/flare/flare.constants";
import { excelStore } from "src/stores/excel-store.interface";
import { MonthsDisplayNoTotal, UnitAllEnum } from "src/entity/common/unit.constant";
import { TableColumnMonthlyProps } from "src/entity/flare/report-voc-emission.entity";
import { monthObject } from "src/entity/common/time.constant";
import { parseFloat4Digit } from "src/utils/number";
import useNavigationMenu from "src/hook/useNavigationMenu";
import { canViewTable } from "src/pages/VocsInventory/Flare/PermissionCheck/PermissionCheck";
import { getFilterWithEmptyMasterdata } from "src/utils/utils-object";
import {
  formatNumber,
  formatNumberWithCommas,
  formatNumberWithRoundUpDecimal,
  parseAndFormatScientificNumber,
  parseAndFormatScientificNumberRemoveComma,
} from "src/utils/process-number";
import { ReportStatusEnum } from "src/entity/report-voc-emission/report-voc-emission.entity";
import { optionsSelectYears } from "src/utils/array";
import Search, { SearchProps } from "antd/es/input/Search";
import { addCenterAlign } from "src/components/AntdCustomPart/TableTitle.tsx/TableTitle";

const { Text } = Typography;
interface ReportVolumeTableProps {}

const ReportVolumeTable: React.FC<ReportVolumeTableProps> = ({}) => {
  const currentYear = new Date().getFullYear();

  const prevCompany: any = useRef();

  const [selectedYear, setSelectedYear] = useState<string>(currentYear.toString());
  const [tableKey, setTableKey] = useState(0);
  const { permissions } = useNavigationMenu();

  // Text Search
  const [textSearch, setTextSearch] = useState<string>("");
  const [searchTrigger, setSearchTrigger] = useState(0);

  const profile = profileStore(useShallow((state) => state.profile));
  const loadingProfile = profileStore(useShallow((state) => state.loading));

  const getAreaInCompany = utilsStore(useShallow((state) => state.getAreaInCompany));
  const dataAreaInCompany = utilsStore(useShallow((state) => state.dataAreaInCompany));

  const setLoadingAll = flareVolumeStore(useShallow((state) => state.setLoadingAll));
  const loadingAll = flareVolumeStore(useShallow((state) => state.loadingAll));

  const getAllHeatingValue = flareVolumeStore(useShallow((state) => state.getAllHeatingValue));
  const dataHeatingValueAll = flareVolumeStore(useShallow((state) => state.dataHeatingValueAll));
  const getAllSettingFlareInformation = flareVolumeStore(useShallow((state) => state.getAllSettingFlareInformation));
  const dataFlareInfo = flareVolumeStore(useShallow((state) => state.dataFlareInfo));
  const loadingGet = reportFlareStore(useShallow((state) => state.loadingGet));
  const searchReportFlareVolume = reportFlareStore(useShallow((state) => state.searchReportFlareVolume));
  const dataReport = reportFlareStore(useShallow((state) => state.dataReport));
  const totalDocs = dataReport.total;
  const getFilterColumnReportFlareVolume = reportFlareStore(useShallow((state) => state.getFilterColumnReportFlareVolume));
  const filterReportFlareVolume = reportFlareStore(useShallow((state) => state.filterReportFlareVolume));

  // const selectedYearExcel = excelStore(useShallow((state) => state.selectedYear));
  const queryStr = excelStore(useShallow((state) => state.queryStr));
  const setSelectedYearExcel = excelStore(useShallow((state) => state.setSelectedYear));
  const setQueryStringExcel = excelStore(useShallow((state) => state.setQueryStr));

  // console.log("[ReportFuelVolume] :: ", { selectedYearExcel, queryStr });
  //   console.log(" filterReportFlareVolume : ", filterReportFlareVolume);
  //   console.log("dataHeatingValueAll : ");

  const {
    onChange,
    filter,
    page,
    setPage,
    loading,
    limit: pageSize,
    setLimit: setPageSize,
    data: sourceData,
    replaceFilter,
  } = usePagination<ResponseReportFlareVolume, FilterReportFlareVolume>({
    action: searchReportFlareVolume,
    remoteData: dataReport.data,
    initialFilter: { year: [selectedYear] },
  });

  // console.log("Source data :: ", sourceData);

  const getMasterLabel = useCallback(
    (type: string, text: string) => {
      if (!text || typeof text != "string") return "";

      if (type === "gasName" && dataHeatingValueAll) {
        const optValue = dataHeatingValueAll.find((item) => item._id === text);
        return optValue?.gasName;
      } else if (type === "area" && dataAreaInCompany) {
        const optValue = dataAreaInCompany.find((item) => item._id === text);
        return optValue?.areaName;
      } else if (type === "flareName" && dataFlareInfo) {
        const optValue = dataFlareInfo.find((item) => item._id === text);
        return optValue?.flareName;
      } else if (type === "unit" && dataHeatingValueAll) {
        const optValue = dataHeatingValueAll.find((item) => item._id === text);
        return optValue?.unitHeatingValue === GAS_UNIT.KCAL_KG ? UnitAllEnum.KG : UnitAllEnum.M3;
      } else if (type === "flareCase") {
        const optValue = flareCaseFilter.find((item) => item.value === text);
        return optValue?.text;
      } else return text;
    },
    [dataFlareInfo, dataAreaInCompany, dataHeatingValueAll],
  );

  const currentFilterExcludePage = useMemo(() => {
    const result = omitBy(filter, (value, key) => key === "page" || key === "year" || key === "limit" || isNull(value));
    return Object.keys(result).length;
  }, [filter]);

  const gasNameFilter = useMemo(() => {
    if (dataReport.data?.length === 0 && currentFilterExcludePage == 0) return [];
    return getFilterWithEmptyMasterdata(dataHeatingValueAll, filterReportFlareVolume.gasName, "gasName");
  }, [dataHeatingValueAll, filterReportFlareVolume.gasName, dataReport.data]);
  const sourceNameFilter = useMemo(() => {
    if (dataReport.data?.length === 0 && currentFilterExcludePage == 0) return [];
    return filterReportFlareVolume.sourceName.map((item) => {
      return {
        text: item,
        value: item,
      };
    });
  }, [filterReportFlareVolume.sourceName, dataReport.data]);
  const areaFilter = useMemo(() => {
    if (dataReport.data?.length === 0 && currentFilterExcludePage == 0) return [];
    return getFilterWithEmptyMasterdata(dataAreaInCompany, filterReportFlareVolume.area, "areaName");
  }, [dataAreaInCompany, filterReportFlareVolume.area, dataReport.data]);
  const flareNameFilter = useMemo(() => {
    if (dataReport.data?.length === 0 && currentFilterExcludePage == 0) return [];
    return getFilterWithEmptyMasterdata(dataFlareInfo, filterReportFlareVolume.flareName, "flareName");
  }, [dataFlareInfo, filterReportFlareVolume.flareName, dataReport.data]);

  const handleTableChange: TableProps<ResponseReportFlareVolume>["onChange"] = (_, filters) => {
    setQueryStringExcel(JSON.stringify(filters));
    onChange(filters);
  };

  const getIndex = (index: number) => (page - 1) * pageSize + index;

  const monthColumns: TableProps<ResponseReportFlareVolume>["columns"] = MonthsDisplayNoTotal.map((item) => {
    return {
      title: item.title,
      key: item.dataIndex,
      render: (_, record) => {
        let res: string = "0";
        if (record[item.dataIndex as keyof TableColumnMonthlyProps].reportStatus === ReportStatusEnum.PROCESSING) {
          res = record[item.dataIndex as keyof TableColumnMonthlyProps]?.value.toString() || "0";
        } else {
          res = record[item.dataIndex as keyof TableColumnMonthlyProps]?.freeze?.value?.toString() || "0";
        }
        // const res =
        //   record[item.dataIndex as keyof TableColumnMonthlyProps].freeze.value ||
        //   record[item.dataIndex as keyof TableColumnMonthlyProps]?.value;
        return <Text>{parseAndFormatScientificNumber(`${res}`, 2)}</Text>;
      },
      align: "left",
    };
  });

  const getSumValueAllMonthlyPerRow = useCallback(
    (record: any) => {
      // console.log("record  :: 0", record);
      const monthsObj: any = monthObject;
      let sumValue = 0;
      for (const key in record) {
        if (monthsObj[key]) {
          const selectedValue =
            record[key as keyof TableColumnMonthlyProps].reportStatus !== ReportStatusEnum.PROCESSING &&
            record[key as keyof TableColumnMonthlyProps].freeze.value
              ? +parseAndFormatScientificNumberRemoveComma(`${record[key as keyof TableColumnMonthlyProps].freeze.value}`)
              : +record[key as keyof TableColumnMonthlyProps]?.value;
          sumValue += selectedValue;
        }
      }

      return parseAndFormatScientificNumber(`${sumValue}`, 2);
    },
    [dataReport.data],
  );

  const columns: TableProps<ResponseReportFlareVolume>["columns"] = [
    {
      title: "No",
      key: "no",
      render: (_, __, index) => getIndex(index + 1),
      align: "left",
    },
    {
      title: "Source Name",
      key: "sourceName",
      render: (_, record) => <Text>{record.details.sourceName}</Text>,
      filters: sourceNameFilter,
      align: "left",
    },
    {
      title: "Gas Name",
      key: "gasName",
      render: (_, record) => <Text>{getMasterLabel("gasName", record.details.gasName)}</Text>,
      filters: gasNameFilter,
      align: "left",
    },
    {
      title: "Area",
      key: "area",
      render: (_, record) => <Text>{getMasterLabel("area", record.details.area)}</Text>,
      filters: areaFilter,
      align: "left",
    },
    {
      title: "Flare Name",
      key: "flareName",
      render: (_, record) => <Text>{getMasterLabel("flareName", record.details.flareName)}</Text>,
      filters: flareNameFilter,
      align: "left",
    },
    {
      title: "Flare Case",
      dataIndex: "flareCase",
      key: "flareCase",
      render: (text) => <Text>{getMasterLabel("flareCase", text)}</Text>,
      filters: flareCaseFilter,
      align: "left",
    },
    {
      title: "Unit",
      key: "unit",
      render: (_, record) => <Text>{record.unit}</Text>,
      // render: (_, record) => <Text>{getMasterLabel("unit", record.details.gasName)}</Text>,
      align: "left",
    },
    ...monthColumns,
    {
      title: "Total",
      render: (_, record) => <Text>{getSumValueAllMonthlyPerRow(record)}</Text>,
      align: "left",
    },
  ];

  const initialRequestDidMount = async (isDidMount?: boolean) => {
    setLoadingAll(true);

    const getAreaInCompanyPromise = getAreaInCompany(profile?.activeCompany._id as string); // area
    const getAllSettingFlareInformationPromise = getAllSettingFlareInformation(); // flare name
    const getAllHeatingValuePromise = getAllHeatingValue(selectedYear); // gas name
    const getFilterColumnReportFlareVolumePromise = getFilterColumnReportFlareVolume(selectedYear);
    const searchReportFlareVolumePromise = searchReportFlareVolume(
      isDidMount === true ? { ...filter, page, limit: pageSize, year: [selectedYear] } : { page, limit: pageSize, year: [selectedYear] },
    );
    await Promise.all([
      getAreaInCompanyPromise,
      getAllSettingFlareInformationPromise,
      getAllHeatingValuePromise,
      getFilterColumnReportFlareVolumePromise,
      searchReportFlareVolumePromise,
    ]);

    setLoadingAll(false);
  };

  useDidMount(() => {
    prevCompany.current = profile?.activeCompany._id;
    initialRequestDidMount(true);
    setQueryStringExcel("");
  });

  useEffect(() => {
    if (profile?.activeCompany) {
      if (prevCompany.current !== profile.activeCompany._id) {
        replaceFilter({ year: [currentYear.toString()] });
        setSelectedYear(currentYear.toString());
        setSelectedYearExcel(currentYear.toString());
        prevCompany.current = profile?.activeCompany._id;
      }
      setPage(1);
      setTableKey(tableKey + 1);
      setTextSearch("");
    }
  }, [profile?.activeCompany, selectedYear]);
  useEffect(() => {
    if (tableKey > 0) {
      initialRequestDidMount(false);
      setQueryStringExcel("");
    }
  }, [tableKey]);

  useEffect(() => {
    searchReportFlareVolume({ ...filter, textSearch, page: 1, limit: pageSize, year: [selectedYear] });
  }, [searchTrigger]);

  const onChangePage = (val: number) => {
    setPage(val);
  };

  const paginationProps: PaginationProps = {
    size: "small",
    pageSize: pageSize,
    current: page,
    total: totalDocs || 0,
    defaultCurrent: 1,
    onChange: onChangePage,
    showSizeChanger: false,
  };

  const handleChangeYear = (year: string) => {
    replaceFilter({ year: [year] });
    setSelectedYearExcel(year);

    setQueryStringExcel(
      queryStr ? JSON.stringify({ ...JSON.parse(queryStr), selectedYear: year }) : JSON.stringify({ selectedYear: year }),
    );
    setSelectedYear(year);
  };
  // console.log("[Render] Page : ", page);

  const onSearch: SearchProps["onSearch"] = (value, _e) => {
    setTextSearch(value);
    setSearchTrigger((prev) => prev + 1);
  };

  return (
    <Row className="w-full" id={"wrap-report-volume-table"}>
      <Row className="w-full" align={"middle"}>
        <Col span={6}>
          <Select value={selectedYear} style={{ width: 120 }} onChange={handleChangeYear} options={optionsSelectYears()} size="middle" />
        </Col>
        <Col span={12} style={{ display: "flex", justifyContent: "center" }}>
          <Search
            onSearch={onSearch}
            style={{ width: 250 }}
            value={textSearch}
            onChange={(e) => setTextSearch(e.target.value)}
            allowClear
          />
        </Col>
        <Col span={6}></Col>
      </Row>
      <Table
        key={tableKey}
        columns={addCenterAlign(columns)}
        dataSource={canViewTable(permissions) ? (sourceData as ResponseReportFlareVolume[]) : []}
        className="w-full mt-4"
        loading={loadingProfile || loadingAll || loading || loadingGet}
        rowKey={(record) => `${record._id}-${record.flareCase}`}
        scroll={{ x: "max-content" }}
        pagination={false}
        onChange={handleTableChange}
      />

      {canViewTable(permissions) ? (
        <Row className="w-full mt-4" justify={"end"}>
          <Pagination {...paginationProps} />
        </Row>
      ) : (
        <></>
      )}
    </Row>
  );
};

export default ReportVolumeTable;
